import { useMemo } from 'react';
import styled from 'styled-components';
import { List, Column } from '@elementary/common-ui/components/Table';
import OverflowTooltip from '@elementary/common-ui/components/OverflowTooltip/OverflowTooltip';
import { numberWithCommas } from '@elementary/common-ui/utils/common';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import UserCard from '../shared/UserCard';
import { SessionRow } from './types';

const ListContainer = styled.div<{ $hideRowBorders: boolean }>`
  ${({ $hideRowBorders }) =>
    $hideRowBorders &&
    `[role='cell'].MuiTableCell-root {
      border-bottom: none;
    }`}
`;

type Props = {
  sessions: SessionRow[];
};

const SessionsList = ({ sessions }: Props) => {
  const columns: Array<Column<SessionRow>> = useMemo(
    () => [
      {
        id: 'title',
        header: 'Session',
        width: 280,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: ({ title }) => (
          <OverflowTooltip>{title ?? '-'}</OverflowTooltip>
        ),
        compareFunc: (a, b) => (a.title ?? '').localeCompare(b.title ?? ''),
      },
      {
        id: 'user',
        header: 'User',
        width: '100%',
        filterable: false,
        showValueWhenEmpty: true,
        renderer: ({ userId, name, email }) => (
          <UserCard
            id={userId}
            name={name ?? email ?? userId}
            email={email ?? ''}
            sx={{ gap: 1.5 }}
          />
        ),
        compareFunc: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
      },
      {
        id: 'startedAt',
        header: 'Started',
        width: 180,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: ({ startedAt }) => (
          <>{localizeDate(startedAt).format('MMM D, h:mm A')}</>
        ),
        compareFunc: (a, b) => a.startedAt.localeCompare(b.startedAt),
      },
      {
        id: 'credits',
        header: 'Credits',
        width: 120,
        filterable: false,
        showValueWhenEmpty: true,
        renderer: ({ credits }) => <>{numberWithCommas(Math.round(credits))}</>,
        compareFunc: (a, b) => a.credits - b.credits,
      },
    ],
    [],
  );

  return (
    <ListContainer $hideRowBorders={sessions.length <= 1}>
      <List
        columns={columns}
        data={sessions}
        idKey="id"
        defaultSortKey="startedAt"
        defaultSortDirection="desc"
      />
    </ListContainer>
  );
};

export default SessionsList;
